/**
 * Market Data Service — India's Stock Market GPT
 *
 * Data sources (no user auth required):
 *   • Yahoo Finance  — stock quotes, OHLC, fundamentals, historical
 *   • NSE India      — live indices (NIFTY 50, SENSEX, BANK NIFTY etc.)
 *
 * In development  : Vite proxy routes /api/yahoo → query1.finance.yahoo.com
 * In production   : Vercel serverless functions in /api/ handle the proxy
 */

// ── Base URLs ─────────────────────────────────────────────────────────────
// In dev: Vite proxy rewrites these. In prod: Vercel /api/* functions.
const YF1  = '/api/yahoo';   // query1.finance.yahoo.com
const YF2  = '/api/yahoo2';  // query2.finance.yahoo.com

const DEFAULT_TIMEOUT = 8000;

async function fetchWithTimeout(url: string, ms = DEFAULT_TIMEOUT): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), ms);
  try {
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(id);
    return res;
  } catch (err) {
    clearTimeout(id);
    throw err;
  }
}

// ── Types ─────────────────────────────────────────────────────────────────

export interface IndexQuote {
  symbol: string;   // "^NSEI"
  name: string;     // "NIFTY 50"
  price: number;
  change: number;
  changePct: number;
  open: number;
  high: number;
  low: number;
  prevClose: number;
}

export interface StockQuote {
  symbol: string;       // "TCS.NS"
  shortName: string;    // "Tata Consultancy Services"
  price: number;
  change: number;
  changePct: number;
  open: number;
  high: number;
  low: number;
  prevClose: number;
  volume: number;
  marketCap: number | null;
  pe: number | null;
  eps: number | null;
  week52High: number | null;
  week52Low: number | null;
  currency: string;
}

export interface OHLCCandle {
  timestamp: number;  // Unix ms
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface StockFundamentals {
  symbol: string;
  pe: number | null;
  pb: number | null;
  eps: number | null;
  roe: number | null;
  debtToEquity: number | null;
  dividendYield: number | null;
  revenueGrowth: number | null;
  profitMargin: number | null;
  marketCap: number | null;
}

// ── NSE Index Symbols (Yahoo Finance tickers) ─────────────────────────────

export const NSE_INDICES: { symbol: string; label: string }[] = [
  { symbol: '^NSEI',     label: 'NIFTY 50'      },
  { symbol: '^BSESN',    label: 'SENSEX'         },
  { symbol: '^NSEBANK',  label: 'BANK NIFTY'     },
  { symbol: '^CNXIT',    label: 'NIFTY IT'       },
  { symbol: '^CNXAUTO',  label: 'NIFTY AUTO'     },
  { symbol: '^INDIAVIX', label: 'INDIA VIX'      },
];

// ── 1. Live Indices (for the market ticker strip) ─────────────────────────

/**
 * Fetch all NSE indices using Yahoo Finance v8 chart API (parallel requests).
 * v7/quote is restricted — v8/chart works without auth and returns live data.
 */
export async function fetchIndices(): Promise<IndexQuote[]> {
  const results = await Promise.allSettled(
    NSE_INDICES.map(async ({ symbol, label }) => {
      const url = `${YF1}/v8/finance/chart/${encodeURIComponent(symbol)}?range=1d&interval=1d`;
      const res  = await fetchWithTimeout(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const meta: any = data?.chart?.result?.[0]?.meta ?? {};
      const price     = meta.regularMarketPrice   ?? 0;
      const prevClose = meta.chartPreviousClose   ?? meta.previousClose ?? 0;
      const change    = price - prevClose;
      const changePct = prevClose ? (change / prevClose) * 100 : 0;
      return {
        symbol,
        name:      label,
        price,
        change,
        changePct,
        open:      meta.regularMarketOpen ?? 0,
        high:      meta.regularMarketDayHigh ?? meta.dayHigh ?? 0,
        low:       meta.regularMarketDayLow  ?? meta.dayLow  ?? 0,
        prevClose,
      } as IndexQuote;
    })
  );

  return results
    .filter((r): r is PromiseFulfilledResult<IndexQuote> => r.status === 'fulfilled')
    .map(r => r.value);
}

// ── 2. Single Stock Quote ─────────────────────────────────────────────────

/**
 * Fetch a live quote for one NSE stock using v8/chart (v7/quote is auth-restricted).
 * Pass the bare symbol e.g. "TCS" — we append ".NS" automatically.
 */
export async function fetchStockQuote(ticker: string): Promise<StockQuote | null> {
  const symbol = ticker.toUpperCase().endsWith('.NS') ? ticker.toUpperCase() : `${ticker.toUpperCase()}.NS`;
  const url = `${YF1}/v8/finance/chart/${encodeURIComponent(symbol)}?range=1d&interval=1d`;

  try {
    const res = await fetchWithTimeout(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const meta: any = data?.chart?.result?.[0]?.meta;
    if (!meta) return null;

    const price     = meta.regularMarketPrice             ?? 0;
    const prevClose = meta.chartPreviousClose ?? meta.previousClose ?? 0;
    const change    = meta.regularMarketChange            ?? (price - prevClose);
    const changePct = meta.regularMarketChangePercent     ?? (prevClose ? (change / prevClose) * 100 : 0);

    return {
      symbol:     meta.symbol                            ?? symbol,
      shortName:  meta.shortName ?? meta.longName        ?? ticker,
      price,
      change,
      changePct,
      open:       meta.regularMarketOpen                 ?? 0,
      high:       meta.regularMarketDayHigh              ?? 0,
      low:        meta.regularMarketDayLow               ?? 0,
      prevClose,
      volume:     meta.regularMarketVolume               ?? 0,
      marketCap:  meta.marketCap                         ?? null,
      pe:         meta.trailingPE                        ?? null,
      eps:        meta.epsTrailingTwelveMonths            ?? null,
      week52High: meta.fiftyTwoWeekHigh                  ?? null,
      week52Low:  meta.fiftyTwoWeekLow                   ?? null,
      currency:   meta.currency                          ?? 'INR',
    };
  } catch (err) {
    console.error(`[marketData] fetchStockQuote(${ticker}) error:`, err);
    return null;
  }
}

// ── 3. Batch Stock Quotes ─────────────────────────────────────────────────

/**
 * Fetch quotes for multiple stocks at once using parallel v8/chart requests.
 * v7/quote is auth-restricted; v8/chart is a per-symbol endpoint so we parallelize.
 * e.g. fetchBatchQuotes(["TCS", "INFY", "RELIANCE"])
 */
export async function fetchBatchQuotes(tickers: string[]): Promise<StockQuote[]> {
  const results = await Promise.allSettled(tickers.map(t => fetchStockQuote(t)));
  return results
    .filter((r): r is PromiseFulfilledResult<StockQuote | null> => r.status === 'fulfilled')
    .map(r => r.value)
    .filter((q): q is StockQuote => q !== null);
}

// ── 4. Historical OHLC (for charts) ──────────────────────────────────────

type Range    = '1d' | '5d' | '1mo' | '3mo' | '6mo' | '1y' | '2y' | '5y';
type Interval = '1m' | '5m' | '15m' | '30m' | '60m' | '1d' | '1wk' | '1mo';

/**
 * Fetch OHLC candle data for a stock or index.
 * e.g. fetchHistorical("TCS", "3mo", "1d")
 */
export async function fetchHistorical(
  ticker: string,
  range: Range = '3mo',
  interval: Interval = '1d'
): Promise<OHLCCandle[]> {
  const symbol = ticker.startsWith('^')
    ? ticker
    : ticker.toUpperCase().endsWith('.NS')
      ? ticker.toUpperCase()
      : `${ticker.toUpperCase()}.NS`;

  const url = `${YF1}/v8/finance/chart/${encodeURIComponent(symbol)}?range=${range}&interval=${interval}&includePrePost=false`;

  try {
    const res = await fetchWithTimeout(url, 12000);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const chart   = data?.chart?.result?.[0];
    if (!chart) return [];

    const timestamps: number[]  = chart.timestamp ?? [];
    const ohlcv                 = chart.indicators?.quote?.[0] ?? {};
    const opens:   number[]     = ohlcv.open   ?? [];
    const highs:   number[]     = ohlcv.high   ?? [];
    const lows:    number[]     = ohlcv.low    ?? [];
    const closes:  number[]     = ohlcv.close  ?? [];
    const volumes: number[]     = ohlcv.volume ?? [];

    return timestamps.map((ts, i) => ({
      timestamp: ts * 1000,
      open:   opens[i]   ?? 0,
      high:   highs[i]   ?? 0,
      low:    lows[i]    ?? 0,
      close:  closes[i]  ?? 0,
      volume: volumes[i] ?? 0,
    })).filter(c => c.close > 0);
  } catch (err) {
    console.error(`[marketData] fetchHistorical(${ticker}) error:`, err);
    return [];
  }
}

// ── 5. Company Fundamentals ───────────────────────────────────────────────

/**
 * Fetch detailed fundamentals via Yahoo Finance quoteSummary.
 * Covers P/E, P/B, ROE, debt/equity, dividend yield, margins etc.
 */
export async function fetchFundamentals(ticker: string): Promise<StockFundamentals | null> {
  const symbol = ticker.toUpperCase().endsWith('.NS') ? ticker.toUpperCase() : `${ticker.toUpperCase()}.NS`;
  const modules = 'summaryDetail,financialData,defaultKeyStatistics';
  const url = `${YF2}/v10/finance/quoteSummary/${encodeURIComponent(symbol)}?modules=${modules}`;

  try {
    const res = await fetchWithTimeout(url, 10000);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const sd   = data?.quoteSummary?.result?.[0]?.summaryDetail    ?? {};
    const fd   = data?.quoteSummary?.result?.[0]?.financialData    ?? {};
    const ks   = data?.quoteSummary?.result?.[0]?.defaultKeyStatistics ?? {};

    return {
      symbol,
      pe:             sd.trailingPE?.raw           ?? null,
      pb:             ks.priceToBook?.raw           ?? null,
      eps:            ks.trailingEps?.raw           ?? null,
      roe:            fd.returnOnEquity?.raw        ?? null,
      debtToEquity:   fd.debtToEquity?.raw          ?? null,
      dividendYield:  sd.dividendYield?.raw         ?? null,
      revenueGrowth:  fd.revenueGrowth?.raw         ?? null,
      profitMargin:   fd.profitMargins?.raw         ?? null,
      marketCap:      sd.marketCap?.raw             ?? null,
    };
  } catch (err) {
    console.error(`[marketData] fetchFundamentals(${ticker}) error:`, err);
    return null;
  }
}

// ── 6. Search / Autocomplete ──────────────────────────────────────────────

export interface SearchResult {
  symbol: string;
  name: string;
  exchange: string;
  type: string;
}

/**
 * Autocomplete search for Indian stocks.
 * e.g. searchStocks("relian") → [{symbol:"RELIANCE.NS", name:"Reliance Industries"...}]
 */
export async function searchStocks(query: string): Promise<SearchResult[]> {
  if (!query || query.length < 2) return [];
  const url = `${YF1}/v1/finance/search?q=${encodeURIComponent(query)}&lang=en-US&region=IN&quotesCount=8&newsCount=0`;

  try {
    const res = await fetchWithTimeout(url, 5000);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const quotes: any[] = data?.quotes ?? [];

    return quotes
      .filter((q: any) => q.quoteType === 'EQUITY' && (q.exchange === 'NSI' || q.exchange === 'BSE'))
      .map((q: any) => ({
        symbol:   q.symbol,
        name:     q.longname ?? q.shortname ?? q.symbol,
        exchange: q.exchange === 'NSI' ? 'NSE' : 'BSE',
        type:     q.typeDisp ?? 'Equity',
      }));
  } catch (err) {
    console.error('[marketData] searchStocks error:', err);
    return [];
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────

/** Format a large number as ₹ with Cr / L suffix */
export function formatIndianCurrency(value: number | null): string {
  if (value == null) return '—';
  if (value >= 1e12) return `₹${(value / 1e12).toFixed(2)} Lakh Cr`;
  if (value >= 1e7)  return `₹${(value / 1e7).toFixed(2)} Cr`;
  if (value >= 1e5)  return `₹${(value / 1e5).toFixed(2)} L`;
  return `₹${value.toLocaleString('en-IN')}`;
}

/** Format a price with ₹ and 2 decimal places */
export function formatPrice(value: number): string {
  return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Format a percentage change with sign and color class */
export function changePctDisplay(pct: number): { text: string; positive: boolean } {
  const positive = pct >= 0;
  return {
    text: `${positive ? '+' : ''}${pct.toFixed(2)}%`,
    positive,
  };
}
